import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Button from '@material-ui/core/Button';
import cx from 'classnames';

import withStyles from '~src/lib/isomorphic-style-loader/withStyles';
import commandUrl from '~src/lib/commandUrl';
import isNarvarInternalUser from '~src/lib/isNarvarInternalUser';
import migrationVariant, { MIGRATION_VARIANT } from '~src/lib/migrationVariant';

import s from './MigrationBanner.scss';

// Must match the default copy in admin-be and neohub word for word.
const DEFAULT_ONBOARDING_MESSAGE =
  'Your account is moving to Narvar Command, the redesigned platform for managing your Narvar experience. If you need to make updates during the move, email narvarcommand@narvar.com so we can ensure your changes carry over.';

const DEFAULT_LIVE_MESSAGE =
  'Your account has moved to Narvar Command, the redesigned platform for managing your Narvar experience. Only changes made in Command are applied to your customer experiences. Your access to Hub will end soon.';

/*
 * The message is brand-authored text, so it is rendered as React text nodes
 * (escaped) and never as HTML. The pattern has to end in a letter, which keeps
 * a sentence's closing period out of the link.
 */
const EMAIL_PATTERN = /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;

// URLs are not linked, so an email inside one (a query parameter, say) must
// not be linked either. The whole whitespace-delimited token is kept as text.
const URL_TOKEN_PATTERN = /(\S*:\/\/\S*)/;

const renderMessage = message =>
  message.split(URL_TOKEN_PATTERN).flatMap((segment, segmentIndex) =>
    segmentIndex % 2 === 1
      ? [segment]
      : segment.split(EMAIL_PATTERN).map((part, index) =>
          index % 2 === 1 ? (
            <a
              // The parts of one fixed string never reorder, so position is identity.
              // eslint-disable-next-line react/no-array-index-key
              key={`${segmentIndex}-${index}`}
              className={s.emailLink}
              href={`mailto:${part}`}
            >
              {part}
            </a>
          ) : (
            part
          ),
        ),
  );

const messageOrDefault = (message, defaultMessage) =>
  message == null ? defaultMessage : message;

/*
 * Hub->Command migration banner (CPMT-5371, CPMT-5481). Driven by the
 * `migration` object hub-backend attaches to each retailer in
 * /api/users/signedin. Not dismissible. When the current retailer is not
 * migrating, renders `children` instead (the default header announcement slot).
 *
 * Still keyed on `is_admin_managed`, so it stays merchant-visible once a
 * retailer goes live in Command — the takeover is gated separately, on
 * `is_hub_access_blocked` (CPMT-5928), and a retailer can be live here while
 * still reaching Hub for a grace period. The Live copy splits by audience
 * (CPMT-5946): merchants get the external grace-period message, Narvar staff
 * the internal one, using the same internal-user check that exempts staff
 * from the takeover.
 *
 * Each banner can be switched off or given a custom message per brand in
 * Command (CPMT-6330). Only an explicit `false` switches a banner off, and a
 * null message falls back to the default copy, so a hub-backend that does not
 * send these fields yet keeps the status-driven behaviour. Switching Live off
 * hides it from Narvar staff too.
 */
const MigrationBanner = ({ migration, isInternalUser, children }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const path = window.location.pathname.split('/')[1];
    if (!['admin'].includes(path)) {
      setIsVisible(true);
    }
  }, []);

  const variant = migrationVariant(migration);

  if (!isVisible || variant === null) {
    return children || null;
  }

  const isBannerOff =
    variant === MIGRATION_VARIANT.LIVE
      ? migration.is_live_banner_enabled === false
      : migration.is_onboarding_banner_enabled === false;

  if (isBannerOff) {
    return children || null;
  }

  if (variant === MIGRATION_VARIANT.LIVE) {
    return (
      <div className={cx(s.banner, s.info)} role="status">
        <div className={s.copyWrap}>
          <span role="img" aria-label="info" className={s.emoji}>
            ℹ️
          </span>
          {isInternalUser ? (
            <p className={s.copy}>
              This account is now on Narvar Command. Only changes made in
              Command are applied to this account&apos;s consumer experiences.
              Please sign in to Command to make changes.
            </p>
          ) : (
            <p className={s.copy}>
              {renderMessage(
                messageOrDefault(
                  migration.live_banner_message,
                  DEFAULT_LIVE_MESSAGE,
                ),
              )}
            </p>
          )}
          <Button
            color="secondary"
            variant="contained"
            href={commandUrl()}
            style={{
              whiteSpace: 'nowrap',
              height: '40px',
              borderRadius: '4px',
              color: '#fff',
            }}
          >
            Go to Command
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cx(s.banner, s.warning)} role="status">
      <div className={s.copyWrap}>
        <span role="img" aria-label="warning" className={s.emoji}>
          ⚠️
        </span>
        <p className={s.copy}>
          {renderMessage(
            messageOrDefault(
              migration.onboarding_banner_message,
              DEFAULT_ONBOARDING_MESSAGE,
            ),
          )}
        </p>
      </div>
    </div>
  );
};

MigrationBanner.propTypes = {
  migration: PropTypes.shape({
    has_started_migration: PropTypes.bool,
    is_admin_managed: PropTypes.bool,
    is_onboarding_banner_enabled: PropTypes.bool,
    onboarding_banner_message: PropTypes.string,
    is_live_banner_enabled: PropTypes.bool,
    live_banner_message: PropTypes.string,
  }),
  isInternalUser: PropTypes.bool,
  children: PropTypes.node,
};

MigrationBanner.defaultProps = {
  migration: null,
  isInternalUser: false,
  children: null,
};

function mapStateToProps(state) {
  const { userReducer: { user } = {} } = state;
  const {
    current_retailer_id: currentRetailerId,
    retailerIdToRetailerInfo: { [currentRetailerId]: retailerInfo } = {},
  } = user || {};

  return {
    migration: (retailerInfo && retailerInfo.migration) || null,
    isInternalUser: isNarvarInternalUser(user),
  };
}

export { MigrationBanner as UnconnectedMigrationBanner };

export default connect(mapStateToProps)(withStyles(s)(MigrationBanner));
